import { ChevronRight, Info } from "lucide-react";
import { DataKeys } from "../../../types";
import { useLang } from "../../Context";
import { StaggerInView, StaggerInViewItem } from "../../tools/Animations";
import { HealthSection } from "../HealthSection";
import { HealthSectionHeader } from "../HealthSectionHeader";
import { getLocalized, HealthLang, LocalizedString } from "../healthTypes";
import { healthAimmaDoctorSectionContentOffset } from "../healthLayout";
import {
    healthClinicalJourneyCardSummary,
    healthPhysicianControlNote,
    healthPhysicianControlNoteIcon,
    healthPhysicianControlNoteText,
} from "../healthVisualStyles";
import {
    COORDINATION_PREVIEW_COMPONENTS,
    COORDINATION_STEP_BADGES,
    COORDINATION_STEP_SURFACES,
} from "./HealthNursingCoordinationPreviews";

interface HealthNursingCoordinationSectionProps {
    data: DataKeys;
}

const ICON_STROKE = 1.5;

const STEP_TITLE_COLORS = [
    "text-teal-700",
    "text-sky-700",
    "text-violet-700",
] as const;

const STEP_COLUMN_START = [
    "min-[960px]:col-start-1",
    "min-[960px]:col-start-3",
    "min-[960px]:col-start-5",
] as const;

const STEP_HEADER_MOBILE_ORDER = ["order-1", "order-3", "order-5"] as const;
const STEP_PREVIEW_MOBILE_ORDER = ["order-2", "order-4", "order-6"] as const;

const CHEVRON_COLUMN_START = [
    "min-[960px]:col-start-2",
    "min-[960px]:col-start-4",
] as const;

export function HealthNursingCoordinationSection({
    data,
}: HealthNursingCoordinationSectionProps) {
    const { lang } = useLang();
    const hLang = lang as HealthLang;
    const panels = (data.panels as DataKeys[]) ?? [];
    const disclaimer = data.disclaimer as LocalizedString | undefined;

    return (
        <HealthSection
            id="coordination"
            ariaLabelledBy="coordination-title"
            compact
        >
            <HealthSectionHeader
                eyebrow={getLocalized(data.eyebrow as LocalizedString, hLang)}
                title={getLocalized(data.title as LocalizedString, hLang)}
                titleId="coordination-title"
                subtitle={getLocalized(data.intro as LocalizedString, hLang)}
                subtitleWide
            />

            <div className={`${healthAimmaDoctorSectionContentOffset} min-w-0 overflow-x-clip`}>
                <StaggerInView className="grid w-full min-w-0 grid-cols-1 gap-3 text-left max-[959px]:gap-y-3 min-[960px]:grid-cols-[minmax(0,1fr)_1.25rem_minmax(0,1fr)_1.25rem_minmax(0,1fr)] min-[960px]:grid-rows-[auto_auto] min-[960px]:gap-x-2 min-[960px]:gap-y-3 xl:gap-x-3">
                                {panels.slice(0, 3).map((panel, index) => {
                                    const badge =
                                        COORDINATION_STEP_BADGES[index];
                                    const titleColor =
                                        STEP_TITLE_COLORS[index] ??
                                        "text-grey-800";
                                    const stepTitle = getLocalized(
                                        panel.title as LocalizedString,
                                        hLang
                                    );
                                    const stepText = getLocalized(
                                        panel.text as LocalizedString,
                                        hLang
                                    );

                                    return (
                                        <StaggerInViewItem
                                            key={`${panel.id as string}-header`}
                                            className={`min-w-0 !w-full text-left ${STEP_HEADER_MOBILE_ORDER[index]} min-[960px]:order-none ${STEP_COLUMN_START[index]} min-[960px]:row-start-1 ${
                                                index > 0 ? "max-[959px]:mt-3" : ""
                                            }`}
                                        >
                                            <div className="flex items-start gap-2.5 text-left">
                                                <span
                                                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border text-sm font-bold ${badge}`}
                                                    aria-hidden="true"
                                                >
                                                    {index + 1}
                                                </span>
                                                <div className="min-w-0 pt-0.5 text-left">
                                                    <h3
                                                        className={`text-left text-base font-semibold leading-snug ${titleColor} [text-align:left]`}
                                                    >
                                                        {stepTitle}
                                                    </h3>
                                                    <p className={healthClinicalJourneyCardSummary}>
                                                        {stepText}
                                                    </p>
                                                </div>
                                            </div>
                                        </StaggerInViewItem>
                                    );
                                })}

                                {CHEVRON_COLUMN_START.map((columnStart, index) => (
                                    <div
                                        key={`coordination-chevron-${index}`}
                                        className={`hidden ${columnStart} min-[960px]:row-start-2 min-[960px]:flex min-[960px]:h-full min-[960px]:items-center min-[960px]:justify-center min-[960px]:self-stretch`}
                                        aria-hidden="true"
                                    >
                                        <ChevronRight
                                            className="h-4 w-4 text-teal-400/80"
                                            strokeWidth={2}
                                        />
                                    </div>
                                ))}

                                {panels.slice(0, 3).map((panel, index) => {
                                    const Preview =
                                        COORDINATION_PREVIEW_COMPONENTS[index];
                                    const surface =
                                        COORDINATION_STEP_SURFACES[index];

                                    return (
                                        <StaggerInViewItem
                                            key={`${panel.id as string}-preview`}
                                            className={`min-w-0 !w-full text-left ${STEP_PREVIEW_MOBILE_ORDER[index]} min-[960px]:order-none ${STEP_COLUMN_START[index]} min-[960px]:row-start-2`}
                                        >
                                            <div
                                                className={`${surface} min-w-0 text-left`}
                                                aria-hidden="true"
                                            >
                                                {Preview ? (
                                                    <Preview lang={hLang} />
                                                ) : null}
                                            </div>
                                        </StaggerInViewItem>
                                    );
                                })}
                            </StaggerInView>

                    {disclaimer ? (
                        <div className={`${healthPhysicianControlNote} mt-6 lg:mt-8`}>
                            <span
                                className={healthPhysicianControlNoteIcon}
                                aria-hidden="true"
                            >
                                <Info
                                    className="h-4 w-4"
                                    strokeWidth={ICON_STROKE}
                                />
                            </span>
                            <p className={healthPhysicianControlNoteText}>
                                {getLocalized(disclaimer, hLang)}
                            </p>
                        </div>
                    ) : null}
            </div>
        </HealthSection>
    );
}
